import { PlusOutlined } from "@ant-design/icons"
import { Button } from "antd"
import React from "react"

import { ActionTooltip } from "../ActionTooltip"

interface TableAddActionProps {
  /** Names the action for this table, e.g. "Add Milestone". Tooltip + accessible name. */
  label: string
  allowed?: boolean
  disabled?: boolean
  onAdd: () => void
  children: React.ReactNode
}

/**
 * Anchors the floating "+" to the bottom-right of a table.
 *
 * `position: sticky` keeps it pinned above the viewport's bottom edge while a
 * table taller than the screen scrolls past. `absolute` would hide it until the
 * reader reached the card's end — on Leads, whose body is sized to fill the
 * screen the card is scrolled to, that is the whole list; `fixed` cannot tell
 * one table from the next.
 *
 * A sticky box is laid out in normal flow and only painted elsewhere, so it
 * would otherwise leave its own height as an empty band under the table — hence
 * `h-0`, with the translate lifting the button back over the card. The 4px it
 * carries past its own height is the whole of the play a 40px button has in a
 * 47px row: less and it sits flush on the card's bottom border, more and its
 * top rides into the table body's horizontal scrollbar.
 *
 * The bottom inset subtracts `--floating-action-inset`, which a scroll
 * container publishes when it reserves padding below its content: sticky travel
 * stops at that content edge, so without it the button halts a whole reserve
 * short of the bottom the reader can see. `FixedContainer` sets it; anywhere
 * else the fallback leaves the plain 12px.
 *
 * The table and the anchor ship as one block rather than two siblings, because
 * a gapped flex parent — the Tasks tab is one — spaces its children whatever
 * their height, and would put the band back as its own gap.
 */
export function TableAddAction({
  label,
  allowed = true,
  disabled = false,
  onAdd,
  children
}: TableAddActionProps) {
  // The same wrapper either way: losing the right mid-session must not change
  // the shape React reconciles, or it rebuilds the table under the reader.
  if (!allowed) return <div>{children}</div>

  return (
    <div>
      {children}
      {/* Keep the action outside the support launcher's expanded footprint.
          The launcher is 56px wide at the right edge; pr-20 leaves another
          24px between the two controls when both are visible. */}
      <div className="sticky bottom-[calc(0.75rem_-_var(--floating-action-inset,0px))] z-10 flex h-0 justify-end pr-20">
        <ActionTooltip title={label} placement="left">
          <Button
            type="primary"
            shape="circle"
            aria-label={label}
            icon={<PlusOutlined />}
            disabled={disabled}
            onClick={onAdd}
            className="size-11 -translate-y-[calc(100%_+_0.25rem)] shadow-lg md:size-10"
          />
        </ActionTooltip>
      </div>
    </div>
  )
}

export default TableAddAction
